import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  CheckCircle2,
  Eye,
  EyeOff,
  FileCheck,
  FileKey,
  Hash,
  Lock,
  Shield,
  ShieldCheck,
  Upload,
  UserRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { CertificadoDeFirma, Emisor } from '@/shared/api/tipos';
import { Campo, Titulo, entrada } from './piezasDelAsistente';

export function PasoCertificado({ datos }: { datos: Emisor }) {
  const clienteDeConsultas = useQueryClient();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [clave, setClave] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useMutation({
    mutationFn: () => {
      const formulario = new FormData();
      formulario.append('archivo', archivo as File);
      formulario.append('clave', clave);
      return api.subir<CertificadoDeFirma>('/emisor/certificado', formulario);
    },
    onSuccess: () => {

      setArchivo(null);
      setClave('');
      setError(null);
      void clienteDeConsultas.invalidateQueries({ queryKey: ['emisor'] });
    },
    onError: (e) =>
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar el certificado'),
  });

  const certificado = datos.certificado ?? null;

  return (
    <div className="grid gap-6">
      {certificado && <EstadoDelCertificado certificado={certificado} />}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="grid content-start gap-4">
          <Titulo
            icono={FileKey}
            color="azul"
            texto={certificado ? 'Reemplazar certificado' : 'Archivo de firma electrónica'}
          />

          <label
            className={[
              'block cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-colors',
              archivo
                ? 'border-marca-300 bg-marca-50/40'
                : 'border-armazon-200 hover:border-marca-300 hover:bg-armazon-50',
            ].join(' ')}
          >
            <input
              type="file"
              accept=".p12,.pfx"
              className="sr-only"
              onChange={(e) => {
                setArchivo(e.target.files?.[0] ?? null);
                setError(null);
              }}
            />
            {archivo ? (
              <span className="flex items-center justify-center gap-3">
                <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-marca-100 text-marca-700">
                  <FileCheck className="size-5" aria-hidden />
                </span>
                <span className="min-w-0 text-left">
                  <span className="block truncate text-sm font-semibold text-armazon-900">
                    {archivo.name}
                  </span>
                  <span className="block text-xs text-armazon-500">
                    {(archivo.size / 1024).toFixed(1)} KB
                  </span>
                </span>
              </span>
            ) : (
              <>
                <span className="mx-auto mb-3 grid size-16 place-items-center rounded-2xl bg-armazon-100">
                  <Upload className="size-7 text-armazon-400" aria-hidden />
                </span>
                <span className="block text-sm font-medium uppercase text-armazon-700">
                  Clic para seleccionar el archivo
                </span>
                <span className="mt-1 block text-xs uppercase text-armazon-500">
                  Formatos aceptados: .p12, .pfx
                </span>
              </>
            )}
          </label>

          {archivo && (
            <button
              type="button"
              onClick={() => setArchivo(null)}
              className="justify-self-start text-xs uppercase tracking-wide text-armazon-500 underline transition-colors hover:text-alerta"
            >
              Quitar el archivo
            </button>
          )}
        </div>

        <div className="grid content-start gap-4">
          <Titulo icono={Lock} color="ambar" texto="Contraseña y validación" />

          <Campo
            etiqueta="Contraseña del certificado"
            requerido
            ayuda="La del archivo, no la del sistema"
          >
            <span className="relative block">
              <input
                type={verClave ? 'text' : 'password'}
                autoComplete="new-password"
                value={clave}
                onChange={(e) => setClave(e.target.value)}
                className={`${entrada} pr-10`}
              />
              <button
                type="button"
                onClick={() => setVerClave(!verClave)}
                aria-label={verClave ? 'Ocultar' : 'Mostrar'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-armazon-400 transition-colors hover:text-armazon-700"
              >
                {verClave ? (
                  <EyeOff className="size-4" aria-hidden />
                ) : (
                  <Eye className="size-4" aria-hidden />
                )}
              </button>
            </span>
          </Campo>

          <ul className="grid gap-2 rounded-xl border border-armazon-200 bg-armazon-50/60 p-4">
            {[
              'Formato PKCS#12 (.p12 o .pfx)',
              'Emitido por una certificadora autorizada',
              'Vigente: uno caducado no autoriza comprobantes',
              `Emitido a nombre del RUC ${datos.ruc}`,
            ].map((requisito) => (
              <li
                key={requisito}
                className="flex items-center gap-2 text-xs uppercase text-armazon-700"
              >
                <CheckCircle2 className="size-3.5 shrink-0 text-armazon-400" aria-hidden />
                {requisito}
              </li>
            ))}
          </ul>

          {error && (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
            >
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={() => cargar.mutate()}
            disabled={!archivo || !clave || cargar.isPending}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Upload className="size-4" aria-hidden />
            {cargar.isPending ? 'Validando y guardando…' : 'Cargar certificado'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EstadoDelCertificado({ certificado }: { certificado: CertificadoDeFirma }) {
  const bien = certificado.vigente && !certificado.porCaducar;

  return (
    <div
      className={[
        'rounded-xl border-2 p-5',
        !certificado.vigente
          ? 'border-red-300 bg-red-50/60'
          : certificado.porCaducar
            ? 'border-amber-300 bg-amber-50/60'
            : 'border-marca-300 bg-marca-50/60',
      ].join(' ')}
    >
      <div className="mb-4 flex items-center gap-3">
        <span
          className={[
            'grid size-10 shrink-0 place-items-center rounded-xl',
            bien
              ? 'bg-marca-100 text-marca-700'
              : certificado.vigente
                ? 'bg-amber-100 text-amber-700'
                : 'bg-red-100 text-alerta',
          ].join(' ')}
        >
          <ShieldCheck className="size-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p
            className={`text-sm font-bold uppercase ${
              bien ? 'text-marca-900' : certificado.vigente ? 'text-amber-900' : 'text-red-900'
            }`}
          >
            Certificado {certificado.vigente ? 'vigente' : 'caducado'}
          </p>
          <p
            className={`text-xs uppercase ${
              bien ? 'text-marca-700' : certificado.vigente ? 'text-amber-800' : 'text-red-800'
            }`}
          >
            {!certificado.vigente
              ? `Venció el ${certificado.validoHasta}. No se pueden autorizar comprobantes.`
              : certificado.porCaducar
                ? `Quedan ${certificado.diasRestantes} días. Renovarlo ante la certificadora no es inmediato.`
                : `Quedan ${certificado.diasRestantes} días.`}
          </p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Dato icono={UserRound} etiqueta="Titular" valor={certificado.titular} />
        <Dato icono={Hash} etiqueta="RUC" valor={certificado.ruc} />
        <Dato icono={Shield} etiqueta="Entidad emisora" valor={certificado.entidadEmisora} />
        <Dato
          icono={CalendarDays}
          etiqueta="Vigencia"
          valor={`${certificado.validoDesde} — ${certificado.validoHasta}`}
        />
      </div>
    </div>
  );
}

function Dato({
  icono: Icono,
  etiqueta,
  valor,
}: {
  icono: LucideIcon;
  etiqueta: string;
  valor: string | null;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <Icono className="mt-0.5 size-3.5 shrink-0 text-armazon-400" aria-hidden />
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-armazon-500">
          {etiqueta}
        </p>
        <p className="truncate text-sm font-medium text-armazon-800" title={valor ?? ''}>
          {valor ?? '—'}
        </p>
      </div>
    </div>
  );
}
